import { CheckCircle2, Circle, Pencil, Printer, Trash2 } from "lucide-react";

import OrderStatusBadge from "@/components/OrderStatusBadge";
import { Button } from "@/components/ui/button";
import { OrderGroupV2, OrderStatusV2, OrderV2 } from "@/lib/types";

const COMPLETED_STATUSES: OrderStatusV2[] = ["delivered", "archived"];

interface OrderGroupTableProps {
  group: OrderGroupV2;
  canPrint: boolean;
  canTransition: boolean;
  canEdit: boolean;
  canDelete: boolean;
  /** deep-review 126：能否進入 /sign 簽收頁（admin/driver）。staff 不可，避免靜默重導。 */
  canSign: boolean;
  onPrintGroup: (groupId: string) => void;
  onTransition: (order: OrderV2, next: OrderStatusV2) => void;
  onEdit: (order: OrderV2) => void;
  onDelete: (order: OrderV2) => void;
  getActionLabel: (order: OrderV2, next: OrderStatusV2) => string;
}

/** 動作鈕 tint 分色（規格 R2-3）：底色/文字/邊皆依「目標階段」語意色，掃視即知下一步。 */
const ACTION_TONE_CLASSES: Record<OrderStatusV2, string> = {
  preparing: "bg-warn-tint text-warn border-warn/25 hover:bg-warn/10",
  ready_to_ship: "bg-progress-tint text-progress border-progress/25 hover:bg-progress/10",
  shipping: "bg-action/10 text-action border-action/25 hover:bg-action/15",
  delivered: "bg-done-tint text-done border-done/25 hover:bg-done/10",
  archived: "bg-raised text-ink-muted border-line hover:bg-ink/5",
  pending: "bg-raised text-ink-muted border-line hover:bg-ink/5",
};

/** 需要簽收頁權限的轉移：自送單轉「已送達」會導向 /sign（admin/driver）。 */
function transitionRequiresSign(order: OrderV2, next: OrderStatusV2) {
  return next === "delivered" && order.shippingMethod === "self_delivery";
}

function isCompleted(status: OrderStatusV2) {
  return COMPLETED_STATUSES.includes(status);
}

function getNextStatuses(status: OrderStatusV2): OrderStatusV2[] {
  const map: Record<OrderStatusV2, OrderStatusV2[]> = {
    pending: ["preparing"],
    preparing: ["ready_to_ship"],
    ready_to_ship: ["shipping"],
    shipping: ["delivered"],
    delivered: ["archived"],
    archived: [],
  };
  return map[status] || [];
}

function formatCreatedAt(value: string | null) {
  if (!value) {
    return "剛建立";
  }

  return new Date(value).toLocaleString("zh-TW", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return "未設定單價";
  }

  // chanli-ops §4：金額全值千分位、NT$ 前綴（NT$1,234,567），禁止壓縮。
  return `NT$${value.toLocaleString("en-US")}`;
}

function getOrderQuantity(order: OrderV2) {
  return order.items.reduce((sum, item) => sum + item.quantity, 0);
}

function getShippingMethodLabel(order: OrderV2) {
  if (order.shippingMethod === "logistics") {
    if (order.carrierName && order.trackingNumber) {
      return `${order.carrierName} / ${order.trackingNumber}`;
    }

    return "物流配送";
  }

  if (order.shippingMethod === "self_delivery") {
    return "自送";
  }

  return "待確認";
}

export default function OrderGroupTable({
  group,
  canPrint,
  canTransition,
  canEdit,
  canDelete,
  canSign,
  onPrintGroup,
  onTransition,
  onEdit,
  onDelete,
  getActionLabel,
}: OrderGroupTableProps) {
  const customerOrders = group.orders;
  const customerActiveCount = customerOrders.filter((order) => !isCompleted(order.status)).length;
  const customerCompletedCount = customerOrders.filter((order) => isCompleted(order.status)).length;
  const allDone = customerOrders.every((order) => isCompleted(order.status));

  return (
    <div
      className={`overflow-hidden rounded-lg border bg-canvas/45 ${
        allDone ? "border-line/80" : "border-progress/25"
      }`}
      data-testid="orderCustomerGroup"
      data-group-id={group.groupId}
      data-order-date={group.orderDate}
      data-customer-name={group.customerName}
    >
      <div className="border-b border-line/80 px-5 py-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              {allDone ? (
                <CheckCircle2 className="h-5 w-5 text-done" />
              ) : (
                <Circle className="h-5 w-5 text-warn" />
              )}
              <h4
                className={`truncate text-base font-bold ${allDone ? "text-ink" : "text-ink-strong"}`}
                title={group.customerName}
              >
                {group.customerName}
              </h4>
            </div>

            <div className="flex flex-wrap gap-2 text-sm">
              <span className="rounded-full bg-surface/80 px-3 py-1 text-ink">{group.orderCount} 筆訂單</span>
              <span className="rounded-full bg-surface/80 px-3 py-1 text-ink">{group.totalQuantity} kg</span>
              <span className="rounded-full bg-surface/80 px-3 py-1 text-ink">待處理 {customerActiveCount}</span>
              {customerCompletedCount > 0 && (
                <span className="rounded-full bg-surface/80 px-3 py-1 text-ink">
                  已完成 {customerCompletedCount}
                </span>
              )}
            </div>
          </div>

          {canPrint && group.orderCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              data-testid="groupPrintBtn"
              onClick={() => onPrintGroup(group.groupId)}
              className="h-11 rounded-md border-line-strong bg-transparent px-4 text-ink hover:bg-raised hover:text-ink"
            >
              <Printer className="mr-1 h-4 w-4" />
              列印群組出貨單
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-3 p-4">
        {customerOrders.map((order, orderIndex) => {
          const done = isCompleted(order.status);
          const orderQuantity = getOrderQuantity(order);

          return (
            <article
              key={order.id}
              className={`rounded-md border p-4 ${
                done ? "border-line/80 bg-surface/40 text-ink-muted" : "border-line/80 bg-surface/80"
              }`}
            >
              {/* 可讀性合約：狀態列＋單行摘要＋單一主動作，次要動作降為圖示鈕。 */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <OrderStatusBadge status={order.status} />
                  <span className="text-xs text-ink-muted">
                    訂單 {orderIndex + 1} · 建立於 {formatCreatedAt(order.createdAt)}
                  </span>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {canEdit && order.status === "pending" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onEdit(order)}
                      aria-label="編輯訂單"
                      title="編輯訂單"
                      className="h-9 w-9 p-0 text-ink-muted hover:bg-raised hover:text-ink"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && order.status === "pending" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`orderDelete-${order.id}`}
                      onClick={() => onDelete(order)}
                      aria-label="刪除訂單"
                      title="刪除訂單"
                      className="h-9 w-9 p-0 text-ink-muted hover:bg-block-tint hover:text-block"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                  {canTransition &&
                    getNextStatuses(order.status).map((next) => {
                      // deep-review 126：自送單「前往簽收」需 /sign 權限（admin/driver）；
                      // staff 無權限時不再顯示可點鈕（點了會被靜默重導首頁），改停用＋提示。
                      const needsSign = transitionRequiresSign(order, next);
                      const blockedForSign = needsSign && !canSign;

                      return (
                        <Button
                          key={next}
                          size="sm"
                          disabled={blockedForSign}
                          aria-disabled={blockedForSign}
                          title={blockedForSign ? "需管理員或司機權限才能進入簽收" : undefined}
                          data-testid={`orderTransition-${order.id}-${next}`}
                          onClick={() => onTransition(order, next)}
                          variant="outline"
                          className={`inline-flex h-11 min-w-[116px] items-center justify-center rounded-md border px-4 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-50 ${ACTION_TONE_CLASSES[next]}`}
                        >
                          {getActionLabel(order, next)}
                        </Button>
                      );
                    })}
                </div>
              </div>

              <p className="mt-2 text-sm text-ink">
                {order.items.length} 項 ·{" "}
                <span className="tabular-nums">{orderQuantity.toFixed(1)} kg</span> ·{" "}
                {getShippingMethodLabel(order)}
              </p>

              <div className="mt-3">
                {order.items.length > 0 ? (
                  <div className="grid min-w-0 gap-2 lg:grid-cols-2">
                    {order.items.map((item, itemIndex) => (
                      <div
                        key={`${order.id}-${item.productCode}-${itemIndex}`}
                        className={`flex min-w-0 items-center justify-between gap-3 rounded-md border border-line/80 bg-canvas/50 px-3 py-2 ${done ? "opacity-70" : ""}`}
                      >
                        <div className="min-w-0">
                          <span className="font-mono text-xs text-progress [overflow-wrap:anywhere]">{item.productCode}</span>
                          <p className={`text-sm font-medium [overflow-wrap:anywhere] ${done ? "text-ink-muted" : "text-ink"}`}>
                            {item.productName}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-sm font-medium tabular-nums text-ink">
                            {item.quantity} {item.unit || "單位"}
                          </p>
                          <p className="text-xs text-ink-muted">{formatMoney(item.unitPrice)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-line/80 bg-canvas/45 px-4 py-4 text-sm text-ink-muted">
                    這筆訂單目前沒有品項資料。
                  </div>
                )}
              </div>

              {order.notes && (
                <p className="mt-3 border-t border-line/80 pt-2 text-sm leading-6 text-ink-muted">
                  備註：{order.notes}
                </p>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
